import { Modal, ModalType, Table, TableRow } from '@code/innovance-react-ui-kit';

import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Document, ProblemTrackingSystemApi } from 'src/front/api/apiInstance';

import {
  DocumentPageUploadData,
  ModelError,
  PtsDocumentsSearchData,
  UploadType,
} from 'src/front/api/generated';
import { displayDocument } from 'src/front/common/utils/domUtils';
import { Separator, TableWrapper, Title } from 'src/front/components/FormStyles';

import { TABLE_WIDTHS } from 'src/front/constant';

import UploadDocumentModal, { UploadDocumentFormData } from './UploadDocumentModal';

interface Props {
  customerId: string;
  inUpdateMode: boolean;
  problemId: string;
  newUploadedDocuments: DocumentPageUploadData[];
  setNewUploadedDocuments: Dispatch<SetStateAction<DocumentPageUploadData[]>>;
}

const UploadDocumentsTableHeaders = [
  'general_components_documentNumber_label',
  'general_components_comment_label',
];

const UploadedDocumentsTable: FC<Props> = ({
  problemId,
  customerId = '',
  inUpdateMode,
  newUploadedDocuments,
  setNewUploadedDocuments,
}) => {
  const { t } = useTranslation();

  const [page, setPage] = useState<number>(1);
  const [uploadError, setUploadError] = useState<ModelError>();
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [isLoadingTable, setIsLoadingTable] = useState<boolean>(false);
  const [selectedDocument, setSelectedDocument] = useState<TableRow>();
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [documents, setDocuments] = useState<PtsDocumentsSearchData[]>([]);
  const [documentTableItems, setDocumentTableItems] = useState<TableRow[]>([]);
  const [uploadedDocuments, setUploadedDocuments] = useState<PtsDocumentsSearchData[]>([]);
  const [documentFormValues, setDocumentFormValues] = useState<UploadDocumentFormData>();

  const loadUploadedDocuments = useCallback(async () => {
    setIsLoadingTable(true);

    const { data } = await ProblemTrackingSystemApi.getPtsDocuments({
      problemId,
      refresh: true,
    });

    if (data.result === 'OK') {
      setUploadedDocuments(data.documentList);
      setDocumentTableItems(
        data.documentList.map((document) => ({
          id: document.documentNumber,
          columns: [document.documentNumber, document.comment],
          editable: !document.isOldDocument,
          deletable: !document.isOldDocument,
        }))
      );
    }

    setIsLoadingTable(false);
  }, [problemId]);

  const loadDocuments = async (pageNumber = 1): Promise<void> => {
    const newDocuments = [...uploadedDocuments, ...documents];
    const pageLimit = pageNumber * 5;
    const pageItems = newDocuments.slice(pageLimit - 5, pageLimit);

    setPage(pageNumber || 1);
    setDocumentTableItems(
      pageItems.map((document) => ({
        id: document.documentNumber,
        columns: [document.documentNumber, document.comment],
        editable: !document.isOldDocument,
        deletable: !document.isOldDocument,
      }))
    );
    setHasNextPage(pageLimit < documents.length);
  };

  const handleIdentitySubmit = async (formData: UploadDocumentFormData): Promise<void> => {
    setUploadError(undefined);

    let newDocuments;
    if (!formData.documentId) {
      const { data } = await Document.uploadDocuments(
        formData.files && formData.files[0],
        'RD',
        UploadType.PTSDOCUMENT,
        formData.explanation,
        customerId
      );
      if (data.result === 'OK') {
        setNewUploadedDocuments((prev) => [
          ...prev,
          {
            createDate: data.createDate,
            documentId: data.documentId,
            documentName: data.documentName,
            documentType: { code: 'RD', value: 'RD' },
            isOldDocument: data.isOldDocument,
            explanation: data.explanation || '',
          },
        ]);
        newDocuments = [
          ...documents,
          {
            documentNumber: data.documentId,
            comment: data.explanation || '',
            name: data.documentName,
            isOldDocument: data.isOldDocument,
          },
        ];
      }
    } else {
      const { data } = await Document.updateDocument({
        documentId: formData.documentId,
        explanation: formData.explanation,
        documentType: 'RD',
        documentName: formData.documentName,
        uploadType: UploadType.PTSDOCUMENT,
      });
      if (data.result === 'OK') {
        setNewUploadedDocuments((prev) =>
          prev.map((doc) =>
            doc.documentId === formData.documentId
              ? {
                  createDate: data.createDate,
                  documentId: data.documentId,
                  documentName: data.documentName,
                  documentType: { code: 'RD', value: 'RD' },
                  isOldDocument: data.isOldDocument,
                  explanation: data.explanation || '',
                }
              : doc
          )
        );
        newDocuments = documents.map((doc) =>
          doc.documentNumber === formData.documentId
            ? {
                documentNumber: data.documentId,
                comment: data.explanation || '',
                name: data.documentName,
                isOldDocument: data.isOldDocument,
              }
            : doc
        );
      }
    }

    setDocuments(newDocuments || []);
    setDocumentTableItems(
      newDocuments
        ? newDocuments.map((document) => ({
            id: document.documentNumber,
            columns: [document.documentNumber, document.comment],
            editable: !document.isOldDocument,
            deletable: !document.isOldDocument,
          }))
        : []
    );
    setShowUploadModal(false);
  };

  useEffect(() => {
    loadUploadedDocuments();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [problemId, inUpdateMode]);

  useEffect(() => {
    loadDocuments();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents]);

  const handleDeleteIdentity = async (): Promise<void> => {
    const { data } = await Document.deleteDocument({
      documentId: selectedDocument?.id,
      uploadType: UploadType.PTSDOCUMENT,
    });
    if (data.result === 'OK') {
      setDocuments(documents.filter((doc) => doc.documentNumber !== selectedDocument?.id));
      setSelectedDocument(undefined);
      setShowDeleteModal(false);
    }
  };

  const addDocument = useCallback((): void => {
    setDocumentFormValues(undefined);
    setShowUploadModal(true);
  }, []);

  const editDocument = useCallback(
    (item?: TableRow): void => {
      const document = newUploadedDocuments.find((doc) => doc.documentId === item?.id);

      setDocumentFormValues(
        Object.assign(document, {
          documentType: { label: 'RD' },
          explanation: document?.explanation || '',
        })
      );
      setShowUploadModal(true);
    },
    [newUploadedDocuments]
  );

  const deleteDocument = useCallback((): void => {
    setShowDeleteModal(true);
  }, []);

  const previewDocument = useCallback(
    async (item?: TableRow): Promise<void> => {
      const document = [...uploadedDocuments, ...documents].find(
        (doc) => doc.documentNumber === item?.id
      );

      if (document) {
        const { data } = await Document.getDocumentData({
          documentId: document.documentNumber,
          documentName: document.name,
        });

        if (data.result === 'OK') {
          displayDocument(data.documentData);
        }
      }
    },
    [documents, uploadedDocuments]
  );

  const actions = useMemo(
    () =>
      inUpdateMode
        ? {
            add: addDocument,
            edit: editDocument,
            delete: deleteDocument,
            preview: previewDocument,
          }
        : {
            preview: previewDocument,
          },
    [addDocument, deleteDocument, editDocument, inUpdateMode, previewDocument]
  );

  const handleSelectDocument = (document: TableRow | undefined): void => {
    setSelectedDocument(document);
  };

  const closeUploadModal = (): void => {
    setUploadError(undefined);
    setShowUploadModal(false);
  };

  return (
    <>
      <Title>
        <Trans i18nKey="general_components_uploadedDocuments_text" />
      </Title>
      <Separator />
      <TableWrapper>
        <Table
          actionConfig={actions}
          addActionText={t('general_add_button')}
          emptyTableText={t('problemTrackingSystem_documentNotFoundSelection_errorMessage')}
          hasNextPage={hasNextPage}
          headers={UploadDocumentsTableHeaders.map((head) => t(head))}
          items={documentTableItems}
          loading={isLoadingTable}
          loadPageItems={loadDocuments}
          onSelect={handleSelectDocument}
          pageNumber={page}
          selectedItem={selectedDocument}
          tableWidth={TABLE_WIDTHS.DESKTOP_3X}
          widths={['30', '70']}
          clearItemOnPageChange
          withRadio
        />
      </TableWrapper>
      {showUploadModal ? (
        <UploadDocumentModal
          error={uploadError}
          formValues={documentFormValues}
          onClose={closeUploadModal}
          submit={handleIdentitySubmit}
        />
      ) : null}
      {showDeleteModal ? (
        <Modal
          cancelText={t('general_cancel_button')}
          description={t('general_deleteRecordConfirm_text')}
          onCancel={() => setShowDeleteModal((state) => !state)}
          onSubmit={handleDeleteIdentity}
          submitText={t('general_components_confirm_button')}
          title="Confirmation"
          type={ModalType.Confirmation}
        />
      ) : null}
    </>
  );
};

export default UploadedDocumentsTable;
